<section class="verification-ui vendor-page">
  @if (extractingInvoice()) {
    <div class="page-upload-overlay" aria-live="polite" aria-busy="true">
      <div class="overlay-card">
        <div class="overlay-spinner" aria-hidden="true"></div>
        <strong>Uploading invoice...</strong>
        <p>Please wait while we read and map invoice data.</p>
      </div>
    </div>
  }
  <div class="page-shell">
    @if (!showForm()) {
      <section class="bill-verification-page landing-view">
        <div class="summary-shell">
          <div class="d-flex align-items-center gap-3">
            <span class="title-badge">
              <lucide-icon [img]="ShieldCheckIcon" [size]="20"></lucide-icon>
            </span>
            <div>
              <h1 class="page-title-lite">Bill Verification</h1>
            </div>
          </div>
          <div class="summary-grid gap-3">
            @for (stat of heroStats(); track stat.label) {
              <article class="summary-tile">
                <div class="summary-tile-header">
                  <div class="summary-tile-label">{{ stat.label }}</div>
                  <div class="summary-tile-value">{{ stat.value }}</div>
                </div>
                <div class="summary-tile-track">
                  @for (segment of stat.segments; track segment.label) {
                    @if (segment.value > 0) {
                      <div
                        [class]="statSegmentClass(segment.tone)"
                        [style.width.%]="statSegmentWidth(segment.value, statSegmentTotal(stat))"
                        [attr.title]="segment.label + ': ' + segment.value"
                      >
                        {{ segment.value }}
                      </div>
                    }
                  }
                </div>
              </article>
            }
          </div>
        </div>

        <div class="table-card">
          <div class="table-toolbar">
            <div class="table-toolbar-main">
              <div class="card-head">
                <lucide-icon [img]="ReceiptTextIcon" [size]="18"></lucide-icon>
                <span>Seller Bills To Generate</span>
              </div>
              <div class="table-toolbar-buttons">
                <button class="btn btn-outline-secondary d-inline-flex align-items-center gap-2" type="button" (click)="toggleSellerBillFilters()">
                  <lucide-icon [img]="FilterIcon" [size]="16"></lucide-icon>
                  <span>Filters</span>
                </button>
                @if (canUploadInvoices()) {
                  <button class="btn btn-primary d-inline-flex align-items-center gap-2" type="button" (click)="openAddInvoice()">
                    <lucide-icon [img]="PlusIcon" [size]="16"></lucide-icon>
                    <span>Add Invoice</span>
                  </button>
                }
              </div>
            </div>
            @if (showSellerBillFilters()) {
              <div class="table-filters">
                <div class="filter-heading">
                  <lucide-icon [img]="FilterIcon" [size]="16"></lucide-icon>
                  <span>Filters</span>
                </div>
                @if (!isVendor()) {
                  <label class="filter-field">
                    <span class="filter-label">Client</span>
                    <select class="form-select filter-control" [ngModel]="generateClientFilterValue" (ngModelChange)="onGenerateClientFilterChange($event)">
                      <option value="">All clients</option>
                      @for (vendor of vendors(); track vendor.id) {
                        <option [value]="vendor.id">{{ vendor.vendor_name }}</option>
                      }
                    </select>
                  </label>
                }
                <label class="filter-field">
                  <span class="filter-label">LOA</span>
                  <select class="form-select filter-control" [ngModel]="generateLoaFilterValue" (ngModelChange)="onGenerateLoaFilterChange($event)">
                    <option value="">All LOAs</option>
                    @for (loa of generateFilterLoas(); track loa.id) {
                      <option [value]="loa.id">{{ loa.loa_number }}</option>
                    }
                  </select>
                </label>
                <label class="filter-field">
                  <span class="filter-label">Bill Cycle</span>
                  <select class="form-select filter-control" [ngModel]="generateBillCycleFilterValue" (ngModelChange)="onGenerateBillCycleFilterChange($event)">
                    <option value="">All cycles</option>
                    @for (cycle of sellerBillCycleOptions(); track cycle) {
                      <option [value]="cycle">{{ cycle }}</option>
                    }
                  </select>
                </label>
                <label class="filter-field">
                  <span class="filter-label">Bill Date From</span>
                  <input class="form-control filter-control" type="date" [ngModel]="generateBillDateFromValue" (ngModelChange)="onGenerateBillDateFromChange($event)" />
                </label>
                <label class="filter-field">
                  <span class="filter-label">Bill Date To</span>
                  <input class="form-control filter-control" type="date" [ngModel]="generateBillDateToValue" (ngModelChange)="onGenerateBillDateToChange($event)" />
                </label>
                <label class="filter-field filter-field-search">
                  <span class="filter-label">Search</span>
                  <input
                    class="form-control filter-control"
                    [ngModel]="generateSearchValue"
                    (ngModelChange)="onGenerateSearchChange($event)"
                    placeholder="Seller, LOA, cycle, status"
                  />
                </label>
                <div class="filter-actions">
                  <button class="btn btn-primary" type="button" (click)="applySellerBillFilters()">Go</button>
                  <button class="btn btn-outline-secondary" type="button" (click)="clearSellerBillFilters()">Clear</button>
                </div>
              </div>
            }
          </div>

          @if (sellerBillTableLoading()) {
            <div class="empty-state">Loading seller bill rows...</div>
          } @else if (!filteredSellerBillRows().length) {
            <div class="empty-state">No seller bill rows found from LOA bill configuration.</div>
          } @else {
            <div class="table-responsive" style="max-height: 500px; overflow-y: auto;">
              <table class="table align-middle mb-0">
                <thead class="sticky-top">
                  <tr>
                    <th>S.No</th>
                    <th>Seller</th>
                    <th>LOA</th>
                    <th>Bill Type</th>
                    <th>Bill Cycle</th>
                    <th>Bill Date</th>
                    <th>Period</th>
                    <th>Approved Quantum</th>
                    <th>Billed Quantum</th>
                    <th>LOA Rate</th>
                    <th>Rate</th>
                    <th>Expected Amount</th>
                    <th>Submitted Amount</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  @for (row of paginatedSellerBillRows(); track row.rowKey; let rowIndex = $index) {
                    <tr>
                      <td>{{ sellerBillSerialNumber(rowIndex) }}</td>
                      <td>{{ row.sellerName }}</td>
                      <td>{{ row.loaNumber }}</td>
                      <td>{{ row.billType }}</td>
                      <td>{{ row.billCycle }}</td>
                      <td>{{ formatDate(row.billDate) }}</td>
                      <td>{{ formatDate(row.billPeriodFrom) }} to {{ formatDate(row.billPeriodTo) }}</td>
                      <td>{{ formatQuantum(row.totalQuantum) }}</td>
                      <td [attr.title]="billedQuantumTooltip(row)">{{ formatQuantum(row.billedQuantum) }}</td>
                      <td>{{ loaRateDisplay(row) }}</td>
                      <td>{{ row.submittedRate ?? '-' }}</td>
                      <td>{{ formatAmount(row.expectedAmount) }}</td>
                      <td>{{ formatAmount(row.submittedAmount) }}</td>
                      <td>
                        <div class="status-stack" [attr.title]="row.statusTooltip">
                          @if (shouldShowVerificationStatus(row)) {
                            <span
                              class="status-badge"
                              [ngClass]="'status-' + statusVariant(displayVerificationStatus(row))"
                            >{{ displayVerificationStatus(row) }}</span>
                          }
                          @if (row.verificationId) {
                            <span
                              class="status-badge"
                              [ngClass]="'status-' + approvalStatusVariant(row.approvalStatus)"
                            >{{ displayApprovalStatus(row) }}</span>
                          }
                          
                        </div>
                      </td>
                      <td>
                        <div class="row-action-group">
                          @if (shouldShowOpenVerification(row)) {
                            <button class="btn btn-sm btn-outline-primary action-pill-btn" type="button" (click)="useSellerBill(row, true)">
                              {{ invoiceActionLabel(row) }}
                            </button>
                          }
                          @if (shouldShowReviewDecision(row)) {
                            <button
                              class="btn btn-sm btn-dark action-pill-btn"
                              type="button"
                              [disabled]="approvalActionLoadingId() === row.verificationId"
                              (click)="openApprovalDecisionDialog(row)"
                            >
                              Review
                            </button>
                          }
                        </div>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
            <div class="pager-row mt-3">
              <div class="pager-meta">
                {{ ((sellerBillsCurrentPage() - 1) * sellerBillsPageSize()) + 1 }}-{{ ((sellerBillsCurrentPage() - 1) * sellerBillsPageSize()) + paginatedSellerBillRows().length }}
                of {{ filteredSellerBillRows().length }} bills
              </div>
              <div class="pagination-shell">
                <button class="pager-btn" type="button" [disabled]="sellerBillsCurrentPage() <= 1" (click)="goToSellerBillsPage(1)" aria-label="First page">«</button>
                <button class="pager-btn" type="button" [disabled]="sellerBillsCurrentPage() <= 1" (click)="goToSellerBillsPage(sellerBillsCurrentPage() - 1)" aria-label="Previous page">‹</button>
                <span class="page-indicator current">{{ sellerBillsCurrentPage() }}</span>
                <button class="pager-btn" type="button" [disabled]="sellerBillsCurrentPage() >= sellerBillTotalPages()" (click)="goToSellerBillsPage(sellerBillsCurrentPage() + 1)" aria-label="Next page">›</button>
                <button class="pager-btn" type="button" [disabled]="sellerBillsCurrentPage() >= sellerBillTotalPages()" (click)="goToSellerBillsPage(sellerBillTotalPages())" aria-label="Last page">»</button>
                <select class="form-select form-select-sm page-size-select pager-size-select" [ngModel]="sellerBillsPageSize()" (ngModelChange)="onSellerBillsPageSizeChange($event)" aria-label="Rows per page">
                  @for (size of sellerBillsPageSizeOptions; track size) {
                    <option [ngValue]="size">{{ size === allSellerBillsPageSize ? 'All' : size + '/Page' }}</option>
                  }
                </select>
              </div>
            </div>
          }
        </div>
      </section>
    } @else {
      <section class="seller-verification-focus">
        <div class="topbar vendor-shell p-3 p-lg-4">
          <div class="d-flex align-items-center gap-3">
            <span class="title-badge">
              <lucide-icon [img]="ShieldCheckIcon" [size]="20"></lucide-icon>
            </span>
            <div class="title-block">
              <h1 class="page-title-lite">Seller Bill Verification</h1>
              <p class="page-subtitle-lite mb-0">Upload invoice details, map bill lines, and verify each seller bill against the same working bill verification flow.</p>
            </div>
            </div>
            <div class="topbar-actions">
              <button class="btn create-btn export" type="button" [disabled]="!showForm()" (click)="closeForm()">
                <lucide-icon [img]="ArrowLeftIcon" [size]="16"></lucide-icon>
                <span>Back to Bill Verification</span>  
              </button>
            </div>
        </div>

        <div class="steps seller-verification-steps">
          <article
            class="step-card seller-step-card"
            [class.seller-step-card--active]="isSellerVerificationStepActive(1)"
            [class.seller-step-card--completed]="isSellerVerificationStepCompleted(1)"
          >
            <div class="step-num">{{ isSellerVerificationStepCompleted(1) ? '✓' : '1' }}</div>
            <div>
              <strong>Upload &amp; Context</strong>
              <p>Upload invoice, preview the file, capture seller, invoice number, date, and verification mode.</p>
            </div>
          </article>
          <article
            class="step-card seller-step-card"
            [class.seller-step-card--active]="isSellerVerificationStepActive(2)"
            [class.seller-step-card--completed]="isSellerVerificationStepCompleted(2)"
          >
            <div class="step-num">{{ isSellerVerificationStepCompleted(2) ? '✓' : '2' }}</div>
            <div>
              <strong>Map Bill Data</strong>
              <p>Map buyer, contract, LOA, billing period, rate, quantum, and amount in a structured format.</p>
            </div>
          </article>
          <article
            class="step-card seller-step-card"
            [class.seller-step-card--active]="isSellerVerificationStepActive(3)"
          >
            <div class="step-num">3</div>
            <div>
              <strong>Review Verification</strong>
              <p>See matched results for seller, LOA, buyers, rate, quantum, and amount before final submission.</p>
            </div>
          </article>
        </div>

        <div class="seller-verification-body" [class.has-uploaded-preview]="hasCurrentInvoiceFile() && showPreviewPanel()">
        <section class="card panel-card seller-upload-panel" [class.seller-upload-panel-half]="hasCurrentInvoiceFile() && showPreviewPanel()" #invoiceFormSection>
          @if (!showBillSection()) {
            <div class="section-head seller-upload-head">
              <div>
                <h2>1. Upload & Context</h2>
                <p>Supports uploaded file preview and dynamic billing structures.</p>
              </div>
            @if (hasCurrentInvoiceFile()) {
              <div class="seller-upload-head-actions">
                <button class="btn create-btn export" title="Preview" type="button" [disabled]="!canPreviewCurrentInvoiceFile()" (click)="previewCurrentInvoiceFile()">
                  <i class="bi bi-eye"></i>
                </button>

                  <button class="btn create-btn export" title="Download" type="button" [disabled]="!canDownloadCurrentInvoiceCopy()" (click)="downloadCurrentInvoiceCopy()">
                    <i class="bi bi-download"></i>
                  </button>

                  @if (!isReadOnlyViewMode()) {
                    <button class="btn create-btn export" title="Delete" type="button" [disabled]="!hasCurrentInvoiceFile()" (click)="replaceSelectedFile()">
                      <i class="bi bi-trash"></i>
                    </button>
                  }
                </div>
              }
            </div>

            @if (showUploadContextContent()) {
              <div class="section-stage-content">

                <div class="field-grid context-grid primary-context-grid">
                  <label class="field">
                    <span>Seller</span>
                    <select class="input" [(ngModel)]="sellerVendorId" (ngModelChange)="onSellerChange()" [disabled]="isVendor() || isReadOnlyViewMode()">
                      @if (!isVendor()) {
                        <option value="">Select seller</option>
                      }
                      @for (vendor of vendors(); track vendor.id) {
                        <option [value]="vendor.id">{{ vendor.vendor_name }}</option>
                      }
                    </select>
                  </label>

                  <label class="field">
                    <span>Invoice Number *</span>
                    <input class="input" [(ngModel)]="invoiceNumber" placeholder="Invoice number" [readonly]="isReadOnlyViewMode()" />
                  </label>

                  <label class="field">
                    <span>Invoice Date *</span>
                    <input class="input" type="date" [(ngModel)]="invoiceDate" [readonly]="isReadOnlyViewMode()" />
                  </label>

                </div>

                @if (visibleExtractedContractLoaCollections().length) {
                  <div class="extracted-contract-collections">
                    <div class="collection-head">
                      <div>
                        <div class="collection-title">Extracted Contract-LOA Collection</div>
                        <div class="collection-subtitle">Mapped contracts, buyers, and LOAs detected from the uploaded invoice.</div>
                      </div>
                      <span class="tag muted collection-count">{{ visibleExtractedContractLoaCollections().length }} Contracts</span>
                    </div>
                    <div class="collection-grid">
                      @for (item of visibleExtractedContractLoaCollections(); track item.contractId || item.contract_id) {
                        <article
                          class="collection-card active view-only"
                        >
                          <div class="collection-card-head">
                            <strong>{{ item.contractNumber || item.contract_number || item.contractId || item.contract_id }}</strong>
                            <span class="tag success">
                              Mapped
                            </span>
                          </div>
                          @if ((item.responderCollection || item.responder_collection || []).length) {
                            <div class="collection-section">
                              <div class="collection-label">Buyers</div>
                              <div class="collection-responders">
                                @for (responder of item.responderCollection || item.responder_collection || []; track responder.responderVendorId || responder.responder_vendor_id) {
                                  <span class="collection-buyer-pill">{{ responder.responderVendorName || responder.responder_vendor_name || responder.responderVendorId || responder.responder_vendor_id }}</span>
                                }
                              </div>
                            </div>
                          }
                          <div class="collection-section">
                            <div class="collection-label">LOAs</div>
                            <div class="collection-loas">
                              @for (loa of item.loaCollection || item.loa_collection || []; track loa.loaId || loa.loa_id) {
                                <span class="tag muted collection-loa-pill">{{ loa.loaNumber || loa.loa_number || loa.loaId || loa.loa_id }}</span>
                              }
                            </div>
                          </div>
                        </article>
                      }
                    </div>
                  </div>
                }

                <div class="step-panel-actions">
                  @if (!editingId() && !isReadOnlyViewMode()) {
                    <button class="btn danger export" type="button" [disabled]="!showUploadContextContent()" (click)="cancelUploadedInvoice()">
                      Cancel
                    </button>
                  }
                  <button class="btn create-btn primary" type="button" [disabled]="extractingInvoice()" (click)="goToBillSection()">
                    Next
                  </button>
                </div>
              </div>
            } @else {
              <div class="upload-zone upload-zone-focus">
                <div class="drop-panel drop-panel-focus">
                  <div class="drop-icon">
                    <lucide-icon [img]="UploadIcon" [size]="28"></lucide-icon>
                  </div>
                  <strong>Drag &amp; drop invoice here</strong>
                  <p>or browse from your system</p>
                  <button class="btn upload-btn" type="button" (click)="invoiceFileInput.click()" [disabled]="extractingInvoice() || hasCurrentInvoiceFile() || isReadOnlyViewMode()">
                    Upload Invoice
                  </button>
                  <input
                    #invoiceFileInput
                    class="file-input-hidden"
                    type="file"
                    (change)="onFileSelected($event)"
                    [disabled]="extractingInvoice() || hasCurrentInvoiceFile() || isReadOnlyViewMode()"
                    accept=".pdf,application/pdf"
                  />
                  <div class="subtext">
                    @if (extractingInvoice()) {
                      Reading invoice and auto-filling fields...
                    } @else {
                      Accepted: PDF only
                    }
                  </div>
                </div>
              </div>
            }
          } @else if (!showVerificationSection()) {
            <div class="section-head seller-upload-head">
              <div>
                <h2>2. Map Bill Data</h2>
                <p>Complete buyer, contract, LOA, and bill values here while keeping the invoice preview on the right.</p>
              </div>
              <div class="seller-upload-head-actions">
                @if (!isReadOnlyViewMode()) {
                  <button
                    class="btn create-btn primary"
                    type="button"
                    title="Add Bill"
                    (click)="openAddSellerBillRowDialog()"
                  >
                    Add Bill
                  </button>
                }
                @if (hasCurrentInvoiceFile()) {
                  <button class="btn create-btn export" title="Preview" type="button" [disabled]="!canPreviewCurrentInvoiceFile()" (click)="previewCurrentInvoiceFile()">
                    <i class="bi bi-eye"></i>
                  </button>

                  <button class="btn create-btn export" title="Download" type="button" [disabled]="!canDownloadCurrentInvoiceCopy()" (click)="downloadCurrentInvoiceCopy()">
                    <i class="bi bi-download"></i>
                  </button>

                  @if (!isReadOnlyViewMode()) {
                    <button class="btn create-btn export" title="Delete" type="button" [disabled]="!hasCurrentInvoiceFile()" (click)="replaceSelectedFile()">
                      <i class="bi bi-trash"></i>
                    </button>
                  }
                }
              </div>
            </div>
            @if (sellerBillTableLoading()) {
              <div class="empty-state">Loading seller bill rows...</div>
            } @else {
              <div class="table-responsive">
                <table class="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Punched Apps</th>
                      <th>Seller</th>
                      <th>Contract</th>
                      <th>LOA</th>
                      <th>Bill Type</th>
                      <th>Bill Cycle</th>
                      <th>Bill Date</th>
                      <th>Period</th>
                      <th>LOA Approved Quantum</th>
                      <th>Billed Quantum</th>
                      <th>LOA Rate</th>
                      <th>Rate</th>
                      <th>Expected Amount</th>
                      <th>Submitted Amount</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    @if (!tableSellerBillRows().length) {
                      <tr>
                        <td class="empty-state justify-content-center" colspan="18">No seller bill rows found from LOA bill configuration.</td>
                      </tr>
                    } @else{
                      @for (row of tableSellerBillRows(); track row.rowKey; let rowIndex = $index) {
                        <tr>
                          <td>{{ rowIndex + 1 }}</td>
                          <td [attr.title]="punchedApplicationTooltip(row)">
                            @if (punchedApplicationCount(row) > 0) {
                              <button class="count-link-btn" type="button" (click)="openApplicationDetails(row)">
                                {{ punchedApplicationCount(row) }}
                              </button>
                            } @else {
                              <span>{{ punchedApplicationCount(row) }}</span>
                            }
                          </td>
                          <td>{{ row.sellerName }}</td>
                          <td>{{ row.contractNumber || '-' }}</td>
                          <td>{{ row.loaNumber }}</td>
                          <td>{{ row.billType }}</td>
                          <td>{{ row.billCycle }}</td>
                          <td>{{ formatDate(row.billDate) }}</td>
                          <td>{{ formatDate(row.billPeriodFrom) }} to {{ formatDate(row.billPeriodTo) }}</td>
                          <td>{{ formatQuantum(row.totalQuantum) }}</td>
                          <td [attr.title]="billedQuantumTooltip(row)">{{ formatQuantum(row.billedQuantum) }}</td>
                          <td>{{ row.configuredRateValue }}</td>
                          <td>{{ row.submittedRate ?? '-' }}</td>
                          <td>{{ formatAmount(row.expectedAmount) }}</td>
                          <td>{{ formatAmount(row.submittedAmount) }}</td>
                          <td>
                            <div class="status-stack" [attr.title]="row.statusTooltip">
                              @if (shouldShowVerificationStatus(row)) {
                                <span
                                  class="status-badge"
                                  [ngClass]="'status-' + statusVariant(displayVerificationStatus(row))"
                                >{{ displayVerificationStatus(row) }}</span>
                              }
                              @if (row.verificationId) {
                                <span
                                  class="status-badge"
                                  [ngClass]="'status-' + approvalStatusVariant(row.approvalStatus)"
                                >{{ displayApprovalStatus(row) }}</span>
                              }
                              @if (sellerBillMismatchDetails(row).length) {
                                <div class="status-difference-list">
                                  @for (detail of sellerBillMismatchDetails(row); track detail.label) {
                                    <div class="status-difference-item">
                                      <span class="status-difference-label">{{ detail.label }}:</span>
                                      <span class="status-difference-value">{{ detail.value }}</span>
                                    </div>
                                  }
                                </div>
                              }
                            </div>
                          </td>
                          <td>
                            <div class="row-action-group">
                              @if (!isBillViewOnlyAdmin() && shouldShowOpenVerification(row)) {
                                <button class="btn create-btn export" type="button" (click)="useSellerBill(row)" title="Edit">
                                  <lucide-icon [img]="PencilIcon" [size]="16"></lucide-icon>
                                </button>
                              }
                              @if (!isBillViewOnlyAdmin() && shouldShowOpenVerification(row)) {
                                <button class="btn create-btn export" title="Delete" type="button" (click)="deleteSellerBill(row)">
                                  <lucide-icon [img]="TrashIcon" [size]="16"></lucide-icon>
                                </button>
                              }
                              @if (shouldShowReviewDecision(row)) {
                                <button
                                  class="btn btn-sm btn-dark action-pill-btn"
                                  type="button"
                                  [disabled]="approvalActionLoadingId() === row.verificationId"
                                  (click)="openApprovalDecisionDialog(row)"
                                >
                                  Review
                                </button>
                              }
                            </div>
                          </td>
                        </tr>
                      }
                    }
                    
                    
                  </tbody>
                </table>
              </div>
            }
            <div class="field-grid context-grid primary-context-grid mb-2">
              <label class="field">
                <span>Buyer</span>
                <select class="input" [(ngModel)]="buyerVendorId" (ngModelChange)="onBuyerChange()" [disabled]="isReadOnlyViewMode()">
                  <option value="">Select buyer</option>
                  @for (vendor of availableBuyers(); track vendor.id) {
                    <option [value]="vendor.id">{{ vendor.vendor_name }}</option>
                  }
                </select>
              </label>

              <label class="field">
                <span>Contract</span>
                <select class="input" [ngModel]="contractId" (ngModelChange)="onContractChange($event)" [disabled]="isReadOnlyViewMode()">
                  <option value="">Select contract</option>
                  @for (contract of contractOptions(); track contract.id) {
                    <option [value]="contract.id">{{ contract.contract_number || contract.id }}</option>
                  }
                </select>
              </label>

              <label class="field">
                <span>Seller LOA</span>
                <select class="input" [ngModel]="loaId" (ngModelChange)="onLoaChange($event)" [disabled]="isReadOnlyViewMode()">
                  <option value="">Select seller LOA</option>
                  @for (loa of availableSellerLoas(); track loa.id) {
                    <option [value]="loa.id">{{ loa.loa_number }}</option>
                  }
                </select>
              </label>

              <label class="field field-wide">
                <span>Remarks</span>
                <div class="table-responsive remarks-table-wrap">
                  <table class="table table-sm align-middle mb-0 remarks-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>LOA Value</th>
                        <th>Submitted Value</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      @if (editingId()) {
                        @for (item of stepTwoReviewRows(); track item.type) {
                          <tr>
                            <td>{{ item.type }}</td>
                            <td>{{ item.loaValue }}</td>
                            <td>{{ item.submittedValue }}</td>
                            <td class="remarks-status-cell">
                              @if (item.status === 'Matched') {
                                <lucide-icon class="remarks-status-icon remarks-status-icon--success" [img]="CheckIcon" [size]="14" [attr.aria-label]="item.status" [attr.title]="item.status"></lucide-icon>
                              } @else {
                                <lucide-icon class="remarks-status-icon remarks-status-icon--danger" [img]="CloseIcon" [size]="14" [attr.aria-label]="item.status" [attr.title]="item.status"></lucide-icon>
                              }
                            </td>
                          </tr>
                        }
                      } @else if (billEntries().length) {
                        @for (entry of billEntries(); track $index; let i = $index) {
                          @for (item of stepTwoReviewRows(entry); track item.type) {
                            <tr>
                              <td>{{ billEntries().length > 1 ? 'Bill Line ' + (i + 1) + ' - ' + item.type : item.type }}</td>
                              <td>{{ item.loaValue }}</td>
                              <td>{{ item.submittedValue }}</td>
                              <td class="remarks-status-cell">
                                @if (item.status === 'Matched') {
                                  <lucide-icon class="remarks-status-icon remarks-status-icon--success" [img]="CheckIcon" [size]="14" [attr.aria-label]="item.status" [attr.title]="item.status"></lucide-icon>
                                } @else {
                                  <lucide-icon class="remarks-status-icon remarks-status-icon--danger" [img]="CloseIcon" [size]="14" [attr.aria-label]="item.status" [attr.title]="item.status"></lucide-icon>
                                }
                              </td>
                            </tr>
                          }
                        }
                      } @else {
                        <tr>
                          <td colspan="4" class="text-center text-muted">No bill data available for review.</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </label>
            </div>

            

            @if (editingId()) {
              <div class="bill-entry-grid">
                <label class="field">
                  <span>Bill Date</span>
                        <input class="input" type="date" [(ngModel)]="invoiceDate" [readonly]="isReadOnlyViewMode()" />
                </label>
                <label class="field">
                  <span>Bill Period From</span>
                        <input class="input" type="date" [ngModel]="billPeriodFrom" (ngModelChange)="onBillPeriodFromChange($event)" [readonly]="isReadOnlyViewMode()" />
                </label>
                <label class="field">
                  <span>Bill Period To</span>
                        <input class="input" type="date" [ngModel]="billPeriodTo" (ngModelChange)="onBillPeriodToChange($event)" [readonly]="isReadOnlyViewMode()" />
                </label>
                <label class="field">
                  <span>Billed Quantum</span>
                        <input class="input" type="number" [(ngModel)]="billedQuantum" (ngModelChange)="updateEditBilledQuantum($event)" [readonly]="isReadOnlyViewMode()" />
                </label>
                <label class="field">
                  <span>Submitted Rate</span>
                        <input class="input" type="number" [(ngModel)]="submittedRate" (ngModelChange)="updateEditSubmittedRate($event)" [readonly]="isReadOnlyViewMode()" />
                </label>
                <label class="field">
                  <span>Submitted Amount</span>
                  <input class="input" type="number" [(ngModel)]="submittedAmount" readonly />
                </label>
              </div>
            } @else {
              <div class="bill-entry-stack">
                @for (entry of billEntries(); track $index; let i = $index) {
                  <div class="bill-entry-card">
                    <div class="bill-entry-card-head">
                      <strong>Bill Line {{ i + 1 }}</strong>
                        @if (billEntries().length > 1 && !isReadOnlyViewMode()) {
                          <button class="btn btn-outline-secondary btn-sm" type="button" (click)="removeBillEntry(i)">Remove</button>
                        }
                    </div>

                    <div class="bill-entry-grid">
                      <label class="field">
                        <span>Bill Date</span>
                        <input class="input" type="date" [ngModel]="entry.billDate" (ngModelChange)="updateBillEntry(i, { billDate: $event })" [readonly]="isReadOnlyViewMode()" />
                      </label>
                      <label class="field">
                        <span>Bill Period From</span>
                        <input class="input" type="date" [ngModel]="entry.billPeriodFrom" (ngModelChange)="updateBillEntry(i, { billPeriodFrom: $event })" [readonly]="isReadOnlyViewMode()" />
                      </label>
                      <label class="field">
                        <span>Bill Period To</span>
                        <input class="input" type="date" [ngModel]="entry.billPeriodTo" (ngModelChange)="updateBillEntry(i, { billPeriodTo: $event })" [readonly]="isReadOnlyViewMode()" />
                      </label>
                      <label class="field">
                        <span>Billed Quantum</span>
                        <input class="input" type="number" [ngModel]="entry.billedQuantum" (ngModelChange)="updateBillEntry(i, { billedQuantum: $event })" [readonly]="isReadOnlyViewMode()" />
                      </label>
                      <label class="field">
                        <span>Submitted Rate</span>
                        <input class="input" type="number" [ngModel]="entry.submittedRate" (ngModelChange)="updateBillEntry(i, { submittedRate: $event })" [readonly]="isReadOnlyViewMode()" />
                      </label>
                      <label class="field">
                        <span>Submitted Amount</span>
                        <input class="input" type="number" [ngModel]="entry.submittedAmount" readonly />
                      </label>
                    </div>
                  </div>
                }

                @if (!isReadOnlyViewMode()) {
                  <div class="bill-section-actions">
                    <button class="btn btn-outline-primary" type="button" (click)="addBillEntry()">Add Bill Line</button>
                  </div>
                }
              </div>
            }
            <div class="step-panel-actions">
              <button class="btn create-btn export" type="button" (click)="backToUploadSection()">
                Back
              </button>
              @if (!editingId() && !isReadOnlyViewMode()) {
                <button class="btn danger export" type="button" [disabled]="!showUploadContextContent()" (click)="cancelUploadedInvoice()">
                  Cancel
                </button>
              }
              <button
                class="btn create-btn primary"
                type="button"
                [disabled]="extractingInvoice()"
                (click)="goToVerificationSection()"
              >
                Next
              </button>
            </div>
          } @else {
            <div class="section-head seller-upload-head">
              <div>
                <h2>3. Review Verification</h2>
                <p>Review the mapped bill details and verification checks before final submission.</p>
              </div>
              <div class="seller-upload-head-actions">
                @if (hasCurrentInvoiceFile()) {
                  <button class="btn create-btn export" title="Preview" type="button" [disabled]="!canPreviewCurrentInvoiceFile()" (click)="previewCurrentInvoiceFile()">
                    <i class="bi bi-eye"></i>
                  </button>

                  <button class="btn create-btn export" title="Download" type="button" [disabled]="!canDownloadCurrentInvoiceCopy()" (click)="downloadCurrentInvoiceCopy()">
                    <i class="bi bi-download"></i>
                  </button>

                  @if (!isReadOnlyViewMode()) {
                    <button class="btn create-btn export" title="Delete" type="button" [disabled]="!hasCurrentInvoiceFile()" (click)="replaceSelectedFile()">
                      <i class="bi bi-trash"></i>
                    </button>
                  }
                }
              </div>
            </div>

              @for (row of reviewSellerBillRows(); track row.rowKey; let i = $index) {
              <div class="verification-summary-card">
                <div class="verification-summary-top">
                  <div>
                    <div class="verification-summary-title">Verification Overview {{ i + 1 }}</div>
                    <div class="verification-summary-copy">
                      {{ row.sellerName }} | {{ row.loaNumber }} | {{ formatDate(row.billPeriodFrom) }} to {{ formatDate(row.billPeriodTo) }}
                    </div>
                  </div>
                  <span class="tag" [ngClass]="reviewRowStatusTone(row)">{{ displayVerificationStatus(row) }}</span>
                </div>

                <div class="verification-check-grid">
                  <div class="verification-check-card">
                    <div class="verification-check-head">
                      <span class="verification-check-label">Seller Matched</span>
                      <span
                        class="verification-match-pill"
                        [ngClass]="{
                          'verification-match-pill--success': reviewRowMatchLabel(row, 'seller') === 'Yes',
                          'verification-match-pill--danger': reviewRowMatchLabel(row, 'seller') !== 'Yes'
                        }"
                      >
                        {{ reviewRowMatchLabel(row, 'seller') }}
                      </span>
                    </div>
                    <strong>{{ row.sellerName }}</strong>
                  </div>
                  <div class="verification-check-card">
                    <div class="verification-check-head">
                      <span class="verification-check-label">LOA Matched</span>
                      <span
                        class="verification-match-pill"
                        [ngClass]="{
                          'verification-match-pill--success': reviewRowMatchLabel(row, 'loa') === 'Yes',
                          'verification-match-pill--danger': reviewRowMatchLabel(row, 'loa') !== 'Yes'
                        }"
                      >
                        {{ reviewRowMatchLabel(row, 'loa') }}
                      </span>
                    </div>
                    <strong>{{ row.loaNumber }}</strong>
                  </div>
                  <div class="verification-check-card">
                    <div class="verification-check-head">
                      <span class="verification-check-label">Invoice Number</span>
                    </div>
                    <strong>{{ invoiceNumber || '-' }}</strong>
                  </div>
                  <div class="verification-check-card">
                    <div class="verification-check-head">
                      <span class="verification-check-label">Rate Matched</span>
                      <span
                        class="verification-match-pill"
                        [ngClass]="{
                          'verification-match-pill--success': reviewRowMatchLabel(row, 'rate') === 'Matched',
                          'verification-match-pill--danger': reviewRowMatchLabel(row, 'rate') === 'Mismatch',
                          'verification-match-pill--info': reviewRowMatchLabel(row, 'rate') === 'Pending'
                        }"
                      >
                        {{ reviewRowMatchLabel(row, 'rate') }}
                      </span>
                    </div>
                    <strong>Expected: {{ loaRateDisplay(row) }}</strong>
                    <strong class="verification-submitted-value">Submitted: {{ row.submittedRate }}</strong>
                    
                  </div>
                  <div class="verification-check-card">
                    <div class="verification-check-head">
                      <span class="verification-check-label">Quantum Matched</span>
                      <span
                        class="verification-match-pill"
                        [ngClass]="{
                          'verification-match-pill--success': reviewRowMatchLabel(row, 'quantum') === 'Matched',
                          'verification-match-pill--danger': reviewRowMatchLabel(row, 'quantum') === 'Mismatch',
                          'verification-match-pill--info': reviewRowMatchLabel(row, 'quantum') === 'Pending'
                        }"
                      >
                        {{ reviewRowMatchLabel(row, 'quantum') }}
                      </span>
                    </div>
                    <strong>Expected: {{ formatQuantum(row.approvedQuantum ?? row.totalQuantum) }}</strong>
                    <strong class="verification-submitted-value">Submitted: {{ formatAmount(row.billedQuantum) }}</strong>
                  </div>
                  
                  
                  <div class="verification-check-card">
                    <div class="verification-check-head">
                      <span class="verification-check-label">Amount Matched</span>
                      <span
                        class="verification-match-pill"
                        [ngClass]="{
                          'verification-match-pill--success': reviewRowMatchLabel(row, 'amount') === 'Matched',
                          'verification-match-pill--danger': reviewRowMatchLabel(row, 'amount') === 'Mismatch',
                          'verification-match-pill--info': reviewRowMatchLabel(row, 'amount') === 'Pending'
                        }"
                      >
                        {{ reviewRowMatchLabel(row, 'amount') }}
                      </span>
                    </div>
                    <strong>Expected: {{ formatAmount(row.expectedAmount ?? row.totalAmount) }}</strong>
                    <strong class="verification-submitted-value">Submitted: {{ formatAmount(row.submittedAmount) }}</strong>
                  </div>
                 
                </div>

                <div class="verification-context-grid d-none">
                 
                  <div class="verification-context-item">
                    <span>Expected Rate</span>
                    <strong>{{ loaRateDisplay(row) }}</strong>
                  </div>
                  <div class="verification-context-item">
                    <span>Expected Quantum</span>
                    <strong>{{ formatQuantum(row.approvedQuantum ?? row.totalQuantum) }}</strong>
                  </div>
                  <div class="verification-context-item">
                    <span>Expected Amount</span>
                    <strong>{{ formatAmount(row.expectedAmount ?? row.totalAmount) }}</strong>
                  </div>
                </div>

                <div class="verification-line-list">
                  <div class="verification-line-item">
                    <div class="verification-line-head">
                      <strong>Bill Line {{ i + 1 }}</strong>
                      <div class="verification-line-status-row">
                        <span class="tag verification-line-status-tag" [ngClass]="reviewRowMatchTone(row, 'rate')">Rate {{ reviewRowMatchLabel(row, 'rate') }}</span>
                        <span class="tag verification-line-status-tag" [ngClass]="reviewRowMatchTone(row, 'quantum')">Quantum {{ reviewRowMatchLabel(row, 'quantum') }}</span>
                        <span class="tag verification-line-status-tag" [ngClass]="reviewRowMatchTone(row, 'amount')">Amount {{ reviewRowMatchLabel(row, 'amount') }}</span>
                      </div>
                    </div>
                    <div class="verification-line-grid">
                      <div><span>Bill Date</span><strong>{{ formatDate(row.billDate) }}</strong></div>
                      <div><span>Period From</span><strong>{{ formatDate(row.billPeriodFrom) }}</strong></div>
                      <div><span>Period To</span><strong>{{ formatDate(row.billPeriodTo) }}</strong></div>
                      <div><span>Billed Quantum</span><strong>{{ formatQuantum(row.billedQuantum) }}</strong></div>
                      <div><span>Submitted Rate</span><strong>{{ row.submittedRate ?? '-' }}</strong></div>
                      <div><span>Submitted Amount</span><strong>{{ formatAmount(row.submittedAmount) }}</strong></div>
                    </div>
                  </div>
                </div>
              </div> 
              }
              

            <div class="step-panel-actions">
              <button class="btn create-btn export" type="button" (click)="backToBillSection()">
                Back
              </button>
              @if (!editingId() && !isReadOnlyViewMode()) {
                <button class="btn danger export" type="button" [disabled]="!showUploadContextContent()" (click)="cancelUploadedInvoice()">
                  Cancel
                </button>
              }
              @if (!isReadOnlyViewMode() || canShowReadOnlyStepThreeAction()) {
                <button class="btn create-btn primary" type="button" [disabled]="saving()" (click)="handleStepThreePrimaryAction()">
                  {{ stepThreePrimaryActionLabel() }}
                </button>
              }
            </div>
          }
        </section>

        @if (hasCurrentInvoiceFile() && showPreviewPanel()) {

            <div class="inline-preview-shell">
              <div class="inline-preview-topbar">
                <div class="inline-preview-title">{{ currentInvoicePreviewTitle() }}</div>
                <div class="inline-preview-badge">
                  <button class="btn seller-preview-close" type="button" title="Hide preview" (click)="hidePreviewPanel()">
                    <lucide-icon [img]="CloseIcon" [size]="18"></lucide-icon>
                  </button>
                </div>
              </div>
              @if (trustedCurrentInvoicePreviewUrl(); as previewUrl) {
                <iframe class="inline-preview-viewer" [src]="previewUrl" title="Invoice PDF Preview"></iframe>
              } @else {
                <div class="inline-preview-empty">Invoice preview is not available.</div>
              }
            </div>
          
        }
        </div>
      </section>
    }
  </div>
</section>

<app-dialog [open]="approvalReasonDialogOpen()" title="Bill Approval Decision" (closed)="closeApprovalReasonDialog()">
  <div class="dialog-body">
    <div class="dialog-summary">
      <div class="approval-summary-main">
        <strong class="approval-summary-title">{{ pendingApprovalInvoiceNumber || 'Selected bill' }}</strong>
        @if (approvalDialogLineSummaries().length) {
          <div class="approval-period-list approval-period-list-inline">
            @for (line of approvalDialogLineSummaries(); track $index) {
              <div class="approval-period-item">
                <div class="approval-period-head">
                  <span class="approval-period-label">
                    LOA {{ line.loaNumber }} | {{ formatDate(line.billPeriodFrom) }} - {{ formatDate(line.billPeriodTo) }}
                  </span>
                  <span class="approval-period-status" [ngClass]="line.tone">{{ line.status }}</span>
                </div>
                <div class="muted-copy approval-period-metrics">
                  Quantum: {{ formatQuantum(line.quantum) }} |
                  Rate: {{ line.rate ?? '-' }} |
                  Amount: {{ formatAmount(line.amount) }}
                </div>
              </div>
            }
          </div>
        } @else if (pendingApprovalSummary) {
          <div class="muted-copy">
            LOA {{ pendingApprovalSummary.loaNumber }} | {{ formatDate(pendingApprovalSummary.billPeriodFrom) }} - {{ formatDate(pendingApprovalSummary.billPeriodTo) }}
          </div>
        }
      </div>
    </div>

    @if (!approvalDialogLineSummaries().length && pendingApprovalSummary) {
      <div class="muted-copy">
        Quantum: {{ formatQuantum(pendingApprovalSummary.quantum) }} |
        Rate: {{ pendingApprovalSummary.rate ?? '-' }} |
        Amount: {{ formatAmount(pendingApprovalSummary.amount) }}
      </div>
    }

    @if (pendingApprovalAllowActionSelection) {
      <div class="mode-switch compact-modes">
        @if (shouldShowApprovalAction('approve')) {
          <button class="mode-card" type="button" [class.active]="pendingApprovalAction === 'approve'" (click)="pendingApprovalAction = 'approve'">
            <h4>Approve</h4>
            <p>{{ approvalActionDescription('approve') }}</p>
          </button>
        }
        @if (shouldShowApprovalAction('reject')) {
          <button class="mode-card" type="button" [class.active]="pendingApprovalAction === 'reject'" (click)="pendingApprovalAction = 'reject'">
            <h4>Reject</h4>
            <p>{{ approvalActionDescription('reject') }}</p>
          </button>
        }
        @if (shouldShowApprovalAction('force-approve')) {
          <button class="mode-card" type="button" [class.active]="pendingApprovalAction === 'force-approve'" (click)="pendingApprovalAction = 'force-approve'">
            <h4>Force Approve</h4>
            <p>{{ approvalActionDescription('force-approve') }}</p>
          </button>
        }
      </div>
    }

    <label class="field">
      <span>Remark *</span>
      <textarea class="input" [(ngModel)]="approvalReason" placeholder="Enter remark"></textarea>
    </label>

    <p class="muted-copy">
      A remark is required before continuing.
    </p>

    <div class="footer-actions">
      <button class="btn" type="button" (click)="closeApprovalReasonDialog()">Cancel</button>
      <button class="btn primary" type="button" [disabled]="saving() || !approvalReason.trim()" (click)="submitApprovalReason()">
        {{ saving() ? 'Submitting...' : approvalDialogSubmitLabel() }}
      </button>
    </div>
  </div>
</app-dialog>

<app-dialog [open]="applicationDetailsDialogOpen()" [title]="applicationDetailsTitle" dialogClass="application-details-dialog" (closed)="closeApplicationDetailsDialog()">
  <div class="dialog-body">
    @if (!applicationDetailItems.length) {
      <div class="empty-state compact">No application details available.</div>
    } @else {
      <div class="table-wrap compact-wrap">
        <table class="compact-table">
          <thead>
            <tr>
              <th>Application</th>
              <th>Buyer</th>
              <th>Contract</th>
              <th>Approved Period</th>
              <th>Approved Quantum</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            @for (item of applicationDetailItems; track item.applicationNumber) {
              <tr>
                <td>{{ item.applicationNumber }}</td>
                <td>{{ item.buyerName }}</td>
                <td>{{ item.contractNumber }}</td>
                <td>{{ item.approvedPeriodLabel }}</td>
                <td>{{ formatQuantum(item.approvedQuantum) }}</td>
                <td>
                  <span class="status-badge" [ngClass]="'status-' + statusVariant(item.status)">{{ item.status }}</span>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }

    <div class="footer-actions">
      <button class="btn" type="button" (click)="closeApplicationDetailsDialog()">Close</button>
    </div>
  </div>
</app-dialog>

<app-dialog [open]="addSellerBillRowDialogOpen()" title="Bill Details" dialogClass="add-row-modal-dialog" (closed)="closeAddSellerBillRowDialog()">
  <div class="dialog-body add-row-dialog-body">
    <div class="dialog-summary add-row-dialog-summary">
      <div>
        <strong>Create Bill</strong>
        <div class="muted-copy">
          Add a seller bill using the same verification theme and field structure used across this workflow.
        </div>
      </div>
    </div>

    <div class="field-grid add-row-dialog-grid">
      <label class="field">
        <span>Buyer</span>
        <select class="input" [(ngModel)]="manualSellerBillRowForm.buyerVendorId">
          <option value="">Select buyer</option>
          @for (vendor of availableBuyers(); track vendor.id) {
            <option [value]="vendor.id">{{ vendor.vendor_name }}</option>
          }
        </select>
      </label>

      <label class="field">
        <span>Contract</span>
        <select class="input" [(ngModel)]="manualSellerBillRowForm.contractId">
          <option value="">Select contract</option>
          @for (contract of contractOptions(); track contract.id) {
            <option [value]="contract.id">{{ contract.contract_number }}</option>
          }
        </select>
      </label>

      <label class="field">
        <span>LOA</span>
        <select class="input" [(ngModel)]="manualSellerBillRowForm.loaId">
          <option value="">Select LOA</option>
          @for (loa of availableSellerLoas(); track loa.id) {
            <option [value]="loa.id">{{ loa.loa_number }}</option>
          }
        </select>
      </label>

      <label class="field">
        <span>Bill Type</span>
        <select class="input" [(ngModel)]="manualSellerBillRowForm.billType">
          <option value="SOP">SOP</option>
          
        </select>
      </label>

      <label class="field">
        <span>Bill Cycle</span>
        <input class="input" [(ngModel)]="manualSellerBillRowForm.billCycle" />
      </label>

      <label class="field">
        <span>Invoice Date</span>
        <input class="input" type="date" [(ngModel)]="manualSellerBillRowForm.invoiceDate" />
      </label>

      <label class="field">
        <span>Bill Period From</span>
        <input class="input" type="date" [(ngModel)]="manualSellerBillRowForm.billPeriodFrom" />
      </label>

      <label class="field">
        <span>Bill Period To</span>
        <input class="input" type="date" [(ngModel)]="manualSellerBillRowForm.billPeriodTo" />
      </label>
      
      <label class="field">
        <span>Bill Date</span>
        <input class="input" type="date" [(ngModel)]="manualSellerBillRowForm.billDate" />
      </label>

      <div class="field add-row-fetch-field add-row-fetch-field-inline">
        <span>&nbsp;</span>
        <button class="btn btn-success text-wrap" type="button" [disabled]="fetchingApproval()" (click)="fetchManualRowApprovalValues()">
          {{ fetchingApproval() ? 'Fetching approval...' : 'Fetch Quantum & Amount from Approval' }}
        </button>
      </div>

      <label class="field add-row-next-row-start">
        <span>Approved Quantum</span>
        <input class="input" [(ngModel)]="manualSellerBillRowForm.approvedQuantum" />
      </label>

      <label class="field">
        <span>Billed Quantum</span>
        <input class="input" [(ngModel)]="manualSellerBillRowForm.billedQuantum" />
      </label>

      <label class="field">
        <span>LOA Rate</span>
        <input class="input" [(ngModel)]="manualSellerBillRowForm.loaRate" />
      </label>

      <label class="field">
        <span>Rate</span>
        <input class="input" [(ngModel)]="manualSellerBillRowForm.submittedRate" />
      </label>

      <label class="field">
        <span>Expected Amount</span>
        <input class="input" [(ngModel)]="manualSellerBillRowForm.expectedAmount" />
      </label>

      <label class="field">
        <span>Submitted Amount</span>
        <input class="input" [(ngModel)]="manualSellerBillRowForm.submittedAmount" />
      </label>
    </div>

    <div class="footer-actions add-row-dialog-actions">
      <button class="btn" type="button" (click)="closeAddSellerBillRowDialog()">Cancel</button>
      <button class="btn primary" type="button" (click)="submitAddSellerBillRow()">Submit</button>
    </div>
  </div>
</app-dialog>
